import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/components/tabs";
import { ScrollArea, ScrollBar } from "@repo/ui/components/scroll-area";
import SectionChild from "~/components/SectionChild";
import Heading2 from "./../__components/heading2";
import Carousel from "~/components/ui/carousel";
import { CarouselItem } from "@repo/ui/components/carousel";
import NewsCard from "./../__components/news-card";
import Hero from '~/components/hero';
import { useTranslation } from 'react-i18next';
import BreadcrumbPageCus from '~/components/breadcrumb-page';
import { useLoaderData } from 'react-router';
import { serverCallApiCMS } from '~/services/serverCallAPI';
import { ICategory, ILandingPage, IPage } from '~/types/types';
import SectionContainer from '~/components/section-container';
import { Card, MiniCard } from '~/components/card';
import useFetchPost from '~/hooks/use-post';
import Pagination from '~/components/pagination';
import { buildSeoMeta, buildCanonicalLink, getOrigin, buildMetaFromLoader } from '~/utils/seo';
import type { Route } from './+types/_index';
import { hostName } from '~/utils/format';
import { StructuredDataScript } from '~/components/structured-data-script';


export async function loader({ request }: Route.LoaderArgs) {
  const [config, category, contentPage] = await Promise.all([
    serverCallApiCMS<ILandingPage>("/api/v1/landing-page/config").catch(() => {
      return { news: [] };
    }),
    serverCallApiCMS<ICategory[]>(
      "/api/v1/categories/list-categories-web"
    ).catch(() => {
      return [];
    }),
    serverCallApiCMS<IPage>("/api/v1/web/pages/tin-tuc").catch(() => {
      return {
        slug: "",
        description: "",
        content: "",
        seo: {
          title: "Tin tức bất động sản KLand",
          meta_description:
            "Cập nhật tin tức, thị trường, dự án và kiến thức bất động sản mới nhất từ KLand để bắt nhịp xu hướng và cơ hội đầu tư.",
          focus_keyphrase: "",
          og_image: [],
        },
        structured: null,
        structuredData: null,
      };
    }),
  ]);

  const origin = getOrigin(request);
  const url = new URL(request.url);
  const canonicalUrl = origin ? `${origin}${url.pathname}` : url.pathname;
  console.log(contentPage);
  return {
    config,
    listCategories: category,
    seo: {
      canonical: canonicalUrl,
      ...contentPage.seo,
    },
    structuredData: contentPage.structuredData ?? null,
  };
}

function FeaturedNews() {
  const { t } = useTranslation("news");
  const data = useLoaderData<typeof loader>();
      const array1 = data.config.news.slice(0, 4);
      const array2 = data.config.news.slice(4);
  return (
    <div className="pt-5 pb-16">
      <SectionChild>
        <Heading2>{t("featured.title")}</Heading2>
        <div className="flex flex-col gap-5 mt-8 md:flex-row">
          <div className="w-full md:w-[560px]">
            <Carousel
              className="w-full pb-5"
              userOptions={{ delay: 5000, playOnInit: false }}
              opts={{
                loop: true,
              }}
              showDot
            >
              {array1.map((item) => {
                return (
                  <CarouselItem
                    className="w-full md:min-w-[560px] h-[320px] md:h-[440px]"
                    key={item.id}
                  >
                    <Card post={item} />
                  </CarouselItem>
                );
              })}
            </Carousel>
          </div>
          <div className="flex-1 hidden grid-cols-2 gap-2 md:grid place-content-between">
            {(array2.length > 0 ? array2 : array1).map((item) => {
              return (
                <div
                  className="w-full max-w-[300px] h-full min-h-[220px]"
                  key={item.id}
                >
                  <MiniCard post={item} />
                </div>
              );
            })}
          </div>
        </div>
      </SectionChild>

      <SectionContainer className="block md:hidden">
        <Carousel className="mt-3">
          {(array2.length > 0 ? array2 : array1).map((item, index) => {
            return (
              <CarouselItem
                className="w-full max-w-[300px] h-[200px]"
                key={index}
              >
                <MiniCard post={item} />
              </CarouselItem>
            );
          })}
        </Carousel>
      </SectionContainer>
    </div>
  );
}


function ListCardNew({ idSlug }: { idSlug: string }) {
  const { data, pagination, onPageChange } = useFetchPost(idSlug);

  const showEmpty = data.length === 0;

  return (
    <div className="flex flex-col gap-8">
      {showEmpty ? (
        <div className="py-10 text-center text-gray-500">
          Hiện chưa có bài viết phù hợp.
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {data.map((n) => (
            <NewsCard key={`${n.id}-${n.id}`} data={n} variant="list" />
          ))}
        </div>
      )}
      <Pagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        pageSize={pagination.pageSize}
        totalItems={pagination.totalRecords}
        onPageChange={onPageChange}
      />
    </div>
  );
}


function AllNews() {
  const { t } = useTranslation("news");
  const { listCategories } = useLoaderData<typeof loader>()
  // const tabs = [
  //   { value: "du-an", label: t("tabs.projects") },
  //   { value: "thi-truong", label: t("tabs.market") },
  //   { value: "cong-ty", label: t("tabs.company") },
  //   { value: "huong-dan", label: t("tabs.guide") },
  // ];

  return (
    <SectionChild className="pt-10 pb-16">
      <Tabs className="mt-5 gap-9" defaultValue={listCategories[0]?.slug || ""}>
        <div className="flex flex-col justify-between md:flex-row">
          <Heading2>{t("all.title")}</Heading2>
          <div>
            <ScrollArea className="whitespace-nowrap scroll-hidden">
              <TabsList className="h-[46px] rounded-none bg-transparent p-0">
                {listCategories.map((item) => {
                  return (
                    <TabsTrigger
                      key={item.id}
                      value={item.slug}
                      className="h-10 p-4 rounded-none data-[state=active]:text-primary data-[state=active]:bg-primary-50 data-[state=active]:border-b-2 data-[state=active]:border-primary border-0 bg-none text-gray-500"
                    >
                      {item.name}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        </div>

        {listCategories.map((tab) => (
          <TabsContent
            value={tab.slug}
            key={tab.id}
          >
            <ListCardNew idSlug={tab.id} />
          </TabsContent>
        ))}
      </Tabs>
    </SectionChild>
  );
}



export default function News() {
  const { t } = useTranslation("news");
  const { structuredData } = useLoaderData<typeof loader>();
  return (
    <div>
      <StructuredDataScript data={structuredData} />
      <Hero
        image="/image/about/hero.png"
        title={t("title")}
        className="max-h-[450px]"
      />
      <div>
        <SectionChild>
          <BreadcrumbPageCus />
        </SectionChild>
      </div>

      <FeaturedNews />
      <SectionChild>
        <AllNews />
      </SectionChild>
    </div>
  );
}
export const handle = {
  dynamicLinks: (props: any) => {
    return buildCanonicalLink(props?.data?.seo?.canonical || "");
  },
};



export const meta = (args: Route.MetaArgs) => {
  return buildMetaFromLoader(
    args,
    {
      hostName,
      buildSeoMeta,
    },
    (ld) => ld
  );
};
